import clsx from "clsx";
import { CheckCircle2, Clock3, MapPin, Pencil, RotateCcw, Send, Trash2 } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";

import type { Direx, DirexIncident, IncidentChanges, IncidentKind, Phase } from "../../api/types";
import { Callout } from "../../components/Callout";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { IncidentCode } from "../../components/IncidentCode";
import { motion, Presence } from "../../components/presence";
import { Select } from "../../components/Select";
import { Badge, Button, Field, inputClass, SectionLabel, Segmented, textareaClass } from "../../components/ui";
import { designate } from "../../components/attention";
import { unitDesignation } from "../../i18n/designation";
import { k, t, tServer } from "../../i18n/i18n";
import { term } from "../../i18n/vocabulary";
import { formatDtg, fromZoneInput, toZoneInput } from "../../map/dtg";
import { KIND_LABEL, STATUS_LABEL, UNASSIGNED } from "./common";
import { ExpectedActions } from "./ExpectedActions";

/** Fiche d'un incident (« Détail de l'incident » de l'iPad) : textes, objectif d'entraînement, réaction
 *  attendue, attendus de l'évaluateur (incidents drones), format d'inject, position ; cible, temps, heure
 *  d'injection et statut réglables ; un incident créé par l'animateur se modifie, tout incident se retire de la
 *  DIREX. Selon le rôle du poste : une cellule marque « traité » les incidents de sa cellule (sans objectif, ni
 *  réaction attendue, ni attendus), l'observateur consulte. */

const TEMPOS: { value: Phase; label: string }[] = [
  { value: "T1", label: "T1" },
  { value: "T2", label: "T2" },
  { value: "T3", label: "T3" },
];
const KINDS: { value: IncidentKind; label: string }[] = [
  { value: "TAC", label: k("Tactique") },
  { value: "TEC", label: k("Technique") },
  { value: "ENV", label: k("Environnement") },
];

function Block({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <SectionLabel inset className="mb-0.5">{label}</SectionLabel>
      <div className="text-title leading-relaxed text-ink">{children}</div>
    </div>
  );
}

/** Heures de l'incident : injection prévue (réglable par l'animateur tant qu'il est prévu), injection,
 *  traitement ; en GDH du fuseau de l'exercice. */
function Chronology({
  incident,
  zone,
  planned,
  editable,
  busy,
  onPlan,
}: {
  incident: DirexIncident;
  zone: string;
  planned: boolean;
  editable: boolean;
  busy: boolean;
  onPlan: (iso: string) => void;
}) {
  const initial = incident.planned_at ? toZoneInput(incident.planned_at, zone) : "";
  const [value, setValue] = useState(initial);
  useEffect(() => setValue(initial), [initial]);
  const commit = () => {
    const iso = fromZoneInput(value, zone);
    if (iso && (!incident.planned_at || Date.parse(iso) !== Date.parse(incident.planned_at))) onPlan(iso);
    else setValue(initial);
  };
  const dtg = (iso: string | null) => (iso ? formatDtg(iso, 0, zone) : null);
  const rows = [
    planned && incident.status !== "prevu" ? { label: t("Injection prévue"), at: dtg(incident.planned_at) } : null,
    { label: t("Injecté"), at: dtg(incident.injected_at) },
    { label: t("Traité"), at: dtg(incident.handled_at) },
  ].filter((r): r is { label: string; at: string | null } => Boolean(r?.at));
  return (
    <div>
      <span className="mb-1 block text-ui font-medium text-ink-soft">{t("Chronologie")}</span>
      {incident.status === "prevu" && planned && (
        editable ? (
          <div className="flex items-center gap-2">
            <span className="w-[190px] shrink-0">
            <input
              type="datetime-local"
              step={300}
              value={value}
              disabled={busy}
              onChange={(e) => setValue(e.target.value)}
              onBlur={commit}
              onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
              aria-label={t("Heure d'injection prévue")}
              className={clsx(inputClass, "tabular-nums")}
            />
            </span>
            <span className="font-mono text-meta whitespace-nowrap text-ink-muted tabular-nums">{dtg(incident.planned_at)}</span>
          </div>
        ) : (
          <p className="text-ui text-ink">
            {t("Injection prévue")} <span className="font-mono tabular-nums">{dtg(incident.planned_at) ?? "—"}</span>
          </p>
        )
      )}
      {rows.length > 0 && (
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-ui">
          {rows.map((r) => (
            <div key={r.label} className="contents">
              <dt className="text-ink-soft">{r.label}</dt>
              <dd className="font-mono text-ink tabular-nums">{r.at}</dd>
            </div>
          ))}
        </dl>
      )}
      {incident.status === "prevu" && planned && editable && (
        <p className="mt-1 text-meta leading-snug text-ink-muted">
          {t("L'horloge l'injecte à cette heure quand l'injection automatique est active.")}
        </p>
      )}
    </div>
  );
}

export function StatusIcon({ status, size = 13 }: { status: DirexIncident["status"]; size?: number }) {
  if (status === "traite") return <CheckCircle2 size={size} className="text-ok" />;
  if (status === "injecte") return <Send size={size} className="text-warn" />;
  return <Clock3 size={size} className="text-ink-muted" />;
}

/** Champs modifiables de la fiche d'incident. */
const EDITED = ["type", "cellule", "short", "detail", "objective", "reaction", "format"] as const;

/** La fiche en cours de modification diffère-t-elle de l'incident ? */
export function incidentEdited(draft: DirexIncident | null, incident: DirexIncident | null): boolean {
  return Boolean(draft && incident && EDITED.some((key) => draft[key] !== incident[key]));
}

/** Modifications de la fiche à envoyer : avec la version lue, ou celle de l'autre poste quand il l'a modifiée
 *  pendant la saisie (l'encadré l'a dit : enregistrer remplace alors sa version en connaissance de cause). */
export function incidentChanges(draft: DirexIncident, incident: DirexIncident): IncidentChanges & { version: number } {
  const stale = draft.version !== incident.version;
  return {
    type: draft.type,
    cellule: draft.cellule,
    short: draft.short.trim() || incident.short,
    detail: draft.detail,
    objective: draft.objective,
    reaction: draft.reaction,
    format: draft.format,
    version: stale ? incident.version : draft.version,
  };
}

export function IncidentPanel({
  incident,
  data,
  busy,
  edit,
  onEdit,
  onChange,
  onDelete,
  onLocate,
}: {
  incident: DirexIncident;
  data: Direx;
  busy: boolean;
  /** Brouillon de modification en cours (`null` : lecture). Tenu par la vue pour survivre à la fermeture du
   *  volet et au passage d'un onglet à l'autre. */
  edit: DirexIncident | null;
  onEdit: (draft: DirexIncident | null) => void;
  /** `done` : appelé une fois la modification acceptée par le serveur (le brouillon n'est vidé qu'à ce moment). */
  onChange: (changes: IncidentChanges, done?: () => void) => void;
  onDelete: () => void;
  onLocate: () => void;
}) {
  const me = data.me;
  const animator = me.role === "animateur";
  const trained = me.role === "cellule";
  const own = trained && incident.cellule === me.cellule;
  const [confirm, setConfirm] = useState(false);
  const editing = edit !== null;
  const draft = edit ?? incident;
  const setDraft = onEdit;
  const custom = incident.source === "custom";
  // Incident proposé par un événement posé sur la carte (docs/08 §19) : textes de Generex, modifiables.
  const fromMap = incident.source === "annotation";
  const editable = custom || fromMap;
  const annotation = incident.annotation;
  // Retiré alors que son annotation est sur la carte : il passe aux écartés (rétablissable), pas supprimé.
  const toDismissed = fromMap && Boolean(annotation && !annotation.orphan);
  // Cible hors de la carte du fait de la composition du dossier : désignation publiée par le serveur, jamais la clé.
  const absentTarget = Boolean(incident.unit_absent || incident.unit_state);
  const targetName = incident.unit_label ? unitDesignation(tServer(incident.unit_label)) : t("L'unité visée");
  // Incident modifié par un autre poste pendant la saisie (relu toutes les 5 s) : l'encadré le dit ; enregistrer
  // remplace alors sa version en connaissance de cause, annuler la fait apparaître.
  const stale = editing && draft.version !== incident.version;
  const save = () =>
    onChange(
      incidentChanges(draft, incident),
      // Brouillon vidé seulement après l'accord du serveur : un refus (réseau, autre poste) garde la saisie.
      () => onEdit(null),
    );

  return (
    <div className={motion("fade")}>
      <div className="space-y-2 border-b border-line px-4 py-3.5">
        <div className="flex items-center gap-2">
          <IncidentCode kind={incident.type} code={incident.code} />
          <span className="text-meta text-ink-muted">
            {t(KIND_LABEL[incident.type])} · {term(incident.cellule)}
          </span>
          <Badge className="ml-auto" tone={incident.status === "traite" ? "ok" : incident.status === "injecte" ? "warn" : "neutral"}>
            {t(STATUS_LABEL[incident.status])}
          </Badge>
        </div>
        {/* Cible hors de la carte du fait de la composition (docs/18 §4.6) : l'incident est gardé ; l'explication et
            l'action attendue sont visibles (pas seulement en bulle, invisible au doigt). */}
        {absentTarget && (
          <div className="space-y-1.5">
            <Badge dot tone="warn">
              {incident.unit_state === "unplaced" ? t("Cible à placer sur la carte") : t("Unité retirée du dossier")}
            </Badge>
            <p className="text-meta leading-snug text-ink-muted">
              {incident.unit_state === "unplaced"
                ? t("{unit} n'est pas sur la carte : placez-la depuis l'ordre de bataille, ou choisissez une autre cible.", {
                    unit: targetName,
                  })
                : t("{unit} a été retirée du dossier : choisissez une autre cible, ou rétablissez l'unité depuis l'ordre de bataille.", {
                    unit: targetName,
                  })}
            </p>
            {animator && !editing && (
              <Button size="sm" variant="secondary" onClick={() => designate('[data-attention="incident-target"] select')}>
                {t("Choisir une autre cible")}
              </Button>
            )}
          </div>
        )}
        {annotation && (
          <div className="space-y-1">
            {annotation.orphan ? (
              <>
                <Badge dot tone="warn">
                  {t("Sans annotation")}
                </Badge>
                <p className="text-meta leading-snug text-ink-muted">
                  {t("L'annotation qui a proposé cet incident a été retirée de la carte ; l'incident reste dans la DIREX.")}
                </p>
              </>
            ) : (
              <p className="flex items-center gap-1.5 text-meta leading-snug text-ink-muted">
                <MapPin size={12} className="shrink-0" />
                {t("Proposé par l'annotation « {name} » de la carte", { name: annotation.label ?? "" })}
              </p>
            )}
          </div>
        )}
        {!annotation && incident.near_annotation && (
          <p className="flex items-center gap-1.5 text-meta leading-snug text-ink-muted">
            <MapPin size={12} className="shrink-0" />
            {t("Près de l'annotation « {name} » ({type}, {distance} m)", {
              name: incident.near_annotation.label,
              type: incident.near_annotation.type_label,
              distance: incident.near_annotation.distance_m,
            })}
          </p>
        )}
        {editing ? (
          <input className={inputClass} value={draft.short} maxLength={200} onChange={(e) => setDraft({ ...draft, short: e.target.value })}
            aria-label={t("Titre de l'incident")} />
        ) : (
          <h3 className="text-title leading-snug font-semibold text-balance text-ink">{incident.short}</h3>
        )}
      </div>

      <div className="space-y-3 border-b border-line px-4 py-3.5">
        {editing ? (
          <>
            <Segmented label={t("Type")} options={KINDS.map((o) => ({ ...o, label: t(o.label) }))} value={draft.type}
              onChange={(type) => setDraft({ ...draft, type })} />
            <Field label={t("Cellule")}>
              <Select className={inputClass} value={draft.cellule} onChange={(e) => setDraft({ ...draft, cellule: e.target.value })}>
                {data.cellules.map((c) => (
                  <option key={c} value={c}>{term(c)}</option>
                ))}
              </Select>
            </Field>
            {(
              [
                ["detail", t("Description"), 4],
                ["objective", t("Objectif d'entraînement"), 2],
                ["reaction", t("Réaction attendue"), 2],
              ] as const
            ).map(([key, label, rows]) => (
              <Field key={key} label={label}>
                <textarea
                  rows={rows}
                  value={draft[key]}
                  onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
                  className={textareaClass}
                />
              </Field>
            ))}
            <Field label={t("Format d'inject")}>
              <input className={inputClass} value={draft.format} maxLength={80} onChange={(e) => setDraft({ ...draft, format: e.target.value })} />
            </Field>
            <Presence show={stale}>
              {stale && (
                <Callout tone="warn" title={t("Modifié par un autre poste pendant votre saisie")}>
                  {t("Votre saisie est gardée. Annuler affiche sa version ; Enregistrer la remplace par la vôtre.")}
                </Callout>
              )}
            </Presence>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => onEdit(null)}>{t("Annuler")}</Button>
              <Button onClick={save} disabled={busy}>{t("Enregistrer")}</Button>
            </div>
          </>
        ) : (
          <>
            {incident.detail && <p className="text-justify text-title leading-relaxed text-ink hyphens-auto">{incident.detail}</p>}
            {incident.objective && <Block label={t("Objectif d'entraînement")}>{incident.objective}</Block>}
            {incident.reaction && <Block label={t("Réaction attendue")}>{incident.reaction}</Block>}
            <div className="grid grid-cols-2 gap-3">
              <Block label={t("Format d'inject")}>{incident.format}</Block>
              <Block label={t("Rôle")}>{term(incident.role_label)}</Block>
            </div>
            <div className="flex items-center gap-2 text-ui text-ink-soft">
              <MapPin size={14} className="shrink-0 text-ink-muted" />
              {incident.mgrs ? (
                <>
                  <span className="font-mono tabular-nums">{incident.mgrs}</span>
                  <Button variant="link" onClick={onLocate} className="ml-auto">
                    {t("Voir sur le croquis")}
                  </Button>
                </>
              ) : (
                <span>{t("Sans position")}</span>
              )}
            </div>
          </>
        )}
      </div>

      {/* Attendus de l'évaluateur (incidents drones) : animateur et observateur ; le serveur n'en envoie pas à la
          cellule entraînée, qui ne les voit donc jamais. */}
      {!editing && !trained && <ExpectedActions items={incident.expected} title={t("Attendus")} />}

      {!editing && !animator && (
        <div className="space-y-3 px-4 py-3.5">
          <Chronology incident={incident} zone={data.time_zone} planned={!trained} editable={false} busy={busy} onPlan={() => {}} />
          {trained ? (
            <div>
              <span className="mb-1 block text-ui font-medium text-ink-soft">{t("Traitement")}</span>
              {own ? (
                incident.status === "injecte" ? (
                  <Button onClick={() => onChange({ status: "traite" })} disabled={busy} highlight>
                    <CheckCircle2 size={14} /> {t("Marquer traité")}
                  </Button>
                ) : (
                  <Button variant="secondary" onClick={() => onChange({ status: "injecte" })} disabled={busy}>
                    <RotateCcw size={14} /> {t("Rouvrir")}
                  </Button>
                )
              ) : (
                <p className="text-ui leading-snug text-ink-muted">
                  {t("Incident de la cellule {cellule} : c'est à elle de le traiter.", { cellule: term(incident.cellule) })}
                </p>
              )}
            </div>
          ) : (
            <p className="text-meta leading-snug text-ink-muted">
              {t("Consultation seule : l'animateur injecte les incidents, les cellules les traitent.")}
            </p>
          )}
        </div>
      )}

      {!editing && animator && (
        <div className="space-y-3 px-4 py-3.5">
          <div data-attention="incident-target">
            <Field label={t("Cible")}>
              <Select
                className={inputClass}
                value={incident.unit_key ?? UNASSIGNED}
                onChange={(e) => onChange(e.target.value === UNASSIGNED ? { clear_unit: true } : { unit_key: e.target.value })}
                disabled={busy}
              >
                {data.targets.map((target) => (
                  <option key={target.key} value={target.key}>
                    {target.commander ? t("{label} (CO)", { label: unitDesignation(target.label) }) : unitDesignation(target.label)}
                  </option>
                ))}
                {absentTarget && incident.unit_key && !data.targets.some((target) => target.key === incident.unit_key) && (
                  <option value={incident.unit_key}>
                    {incident.unit_state === "unplaced"
                      ? t("{label} (à placer)", { label: targetName })
                      : t("{label} (retirée)", { label: targetName })}
                  </option>
                )}
                <option value={UNASSIGNED}>{t("Éventuel (sans cible)")}</option>
              </Select>
            </Field>
          </div>
          <Segmented label={t("Temps")} options={TEMPOS} value={incident.tempo} onChange={(tempo) => onChange({ tempo })} />
          <Chronology incident={incident} zone={data.time_zone} planned editable busy={busy}
            onPlan={(planned_at) => onChange({ planned_at })} />
          <div>
            <span className="mb-1 block text-ui font-medium text-ink-soft">{t("Animation")}</span>
            <div className="flex flex-wrap gap-2">
              {incident.status === "prevu" && (
                <Button onClick={() => onChange({ status: "injecte" })} disabled={busy} highlight>
                  <Send size={14} /> {t("Injecter")}
                </Button>
              )}
              {incident.status === "injecte" && (
                <Button onClick={() => onChange({ status: "traite" })} disabled={busy} highlight>
                  <CheckCircle2 size={14} /> {t("Marquer traité")}
                </Button>
              )}
              {incident.status !== "prevu" && (
                <Button variant="secondary" onClick={() => onChange({ status: "prevu" })} disabled={busy}>
                  <RotateCcw size={14} /> {t("Remettre à prévu")}
                </Button>
              )}
            </div>
          </div>
          <div className="flex items-center justify-between border-t border-line pt-3">
            {editable ? (
              <Button size="sm" variant="secondary" onClick={() => onEdit(incident)}>
                <Pencil size={14} /> {t("Modifier")}
              </Button>
            ) : (
              <span className="text-meta text-ink-muted">
                {incident.auto ? t("Incident du catalogue, posé par la génération") : t("Incident du catalogue")}
              </span>
            )}
            <Button size="sm" variant="danger-ghost" onClick={() => setConfirm(true)}>
              <Trash2 size={14} /> {custom ? t("Supprimer") : t("Retirer")}
            </Button>
          </div>
        </div>
      )}

      <Presence show={confirm}>
        {confirm && (
          <ConfirmDialog
            title={custom ? t("Supprimer cet incident ?") : t("Retirer cet incident de la DIREX ?")}
            confirmLabel={custom ? t("Supprimer") : t("Retirer")}
            busy={busy}
            busyLabel={custom ? t("Suppression…") : t("Retrait…")}
            onConfirm={() => {
              setConfirm(false);
              onDelete();
            }}
            onCancel={() => setConfirm(false)}
          >
            {toDismissed
              ? t("« {title} » quittera la DIREX et rejoindra les incidents écartés ; vous pourrez le rétablir tant que son annotation reste sur la carte.", {
                  title: incident.short,
                })
              : custom || fromMap
              ? t("« {title} » sera supprimé définitivement de ce dossier.", { title: incident.short })
              : t(
                  "« {title} » quittera la DIREX de ce dossier ; il reste au catalogue et pourra revenir à la prochaine génération.",
                  { title: incident.short },
                )}
          </ConfirmDialog>
        )}
      </Presence>
    </div>
  );
}
